import { CartItem as CartItemModel } from "@omni/types/api/cart/bff/shared/cartItem.model";
import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { CloudStoreType } from "@omni/types/api/store/cloud/shared/enums/storeType.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { cn } from "@omni/utils/styles";
import React, { type ReactNode, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { Row } from "@/components/General/Grid/row.component";
import { StorePickerModal } from "@/components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@/components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@/components/Store/StorePicker/types/storePickerType.enum";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.cta.selectors";
import { APIStatus } from "@/redux/modules/api/data/cart/cart.module";
import { AddToWishlistStatus } from "@/redux/modules/api/data/wishlist/@types/addToWishlistStatus.enum";
import { getWishlists } from "@/redux/modules/api/data/wishlist/wishlist.selectors";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getWishlistModalVisible } from "@/redux/modules/value/wishlistModal/wishlistModal.selectors";

import CartItem from "./cartItem/cartItem.component";

type Props = {
    icon: string;
    title: string;
    infoBlock?: string | ReactNode;
    cartItems: Array<CartItemModel>;
    deliveryType: string;
    removeFromCart: (
        id: string,
        quantity: number,
        isFreeAddedByCustomer: boolean
    ) => Promise<any>;
    addAdditionalService: (
        cartItemId: string,
        serviceSku: string,
        product: CartItemModel
    ) => Promise<any>;
    addAccessory: (
        cartItemId: string,
        sku: string,
        product: Product<ProductFieldSet.ProductTile>
    ) => Promise<any>;
    removeAdditionalService: (cartItemId: string) => Promise<any>;
    updateCartItem: (
        id: string,
        newQuantity: number,
        deliveryType: string,
        product?: CartItemModel,
        quantityDelta?: number
    ) => Promise<any>;
    updateCartItemConfig: (
        id: string,
        config: Record<string, unknown>,
        product: CartItemModel,
        productSubType: string
    ) => Promise<any>;
    addToWishlist: (sku: string) => Promise<AddToWishlistStatus>;
    removeFromWishlist: (sku: string) => Promise<any>;
    ccTooltipInfo: string;
    dsvTooltipInfo: string;
    warrantyDepositTooltipInfo: string;
    cashbackTooltipInfo: string;
    changeStore?: (store: string) => Promise<any>;
    preferredStore?: string;
    onChangeStore?: (sku: string) => void;
    paintConfiguratorPath?: string;
};

const ProductCollectionBlock = ({
    icon,
    title,
    infoBlock,
    cartItems,
    deliveryType,
    removeFromCart,
    updateCartItem,
    updateCartItemConfig,
    ccTooltipInfo,
    dsvTooltipInfo,
    warrantyDepositTooltipInfo,
    cashbackTooltipInfo,
    changeStore,
    preferredStore,
    onChangeStore,
    addToWishlist,
    removeFromWishlist,
    addAdditionalService,
    addAccessory,
    removeAdditionalService,
    paintConfiguratorPath
}: Props): React.ReactElement<"div"> | null => {
    const { t } = useGranite();
    const [storePickerSku, setStorePickerSku] = useState<string>("");
    const [isUpdatingCart, setIsUpdatingCart] = useState<{
        [key: string]: boolean;
    }>({});
    const wishlists = useSelector(getWishlists);
    const callToActions = useSelector(
        CartSelectors.getProductsWithCallToActions,
        shallowEqual
    );
    const wishlistModalVisible = useSelector(getWishlistModalVisible);
    const addToWishlistQueue = useRef<CartItemModel[]>([]);

    const determineQuantity = (
        deliveryType: string,
        id: string,
        minimumOrderQuantity: number
    ): number => {
        const quantity = getOldQuantityForId(cartItems, id);
        if (deliveryType === DeliveryType.ClickAndCollect) {
            return quantity > minimumOrderQuantity
                ? quantity
                : minimumOrderQuantity;
        }

        return quantity;
    };

    const changeDeliveryType = (
        deliveryType: string,
        cartItem: CartItemModel
    ): void => {
        const newQuantity = determineQuantity(
            cartItem.deliveryType,
            cartItem.id!,
            cartItem.product?.minimumOrderQuantity!
        );

        if (deliveryType === DeliveryType.HomeDelivery) {
            setIsUpdatingCartForId(cartItem.id!, true);
            updateCartItemForNewDeliveryMethod();
        } else if (preferredStore && changeStore) {
            setIsUpdatingCartForId(cartItem.id!, true);
            changeStore(preferredStore).then((action) => {
                action.type === APIStatus.SUCCESS
                    ? updateCartItemForNewDeliveryMethod()
                    : setIsUpdatingCartForId(cartItem.id!, false);
            });
        } else if (onChangeStore) {
            onChangeStore(cartItem.sku);
            setStorePickerSku(cartItem.sku);
        }

        function updateCartItemForNewDeliveryMethod() {
            updateCartItem(cartItem.id!, newQuantity, deliveryType).then(
                (action) => {
                    if (action.type === APIStatus.SUCCESS) {
                        if (
                            deliveryType === DeliveryType.HomeDelivery &&
                            cartItem.quantity <
                                cartItem.product?.minimumOrderQuantity!
                        ) {
                            broadcastMessage({
                                id: "general-cart",
                                text: t(
                                    "general.cart.message.quantityChangedToMinimumQuantity"
                                ),
                                status: "warning",
                                dismissible: true
                            });
                        }
                    }

                    setIsUpdatingCartForId(cartItem.id!, false);
                }
            );
        }
    };

    const setIsUpdatingCartForId = (id: string, isUpdatingCart: boolean) => {
        setIsUpdatingCart((cur: any) => ({
            ...cur.isUpdatingCart,
            [id]: isUpdatingCart
        }));
    };

    const hasProductMultipleCartEntries = (sku: string) => {
        return cartItems.filter((p) => p.sku === sku)?.length > 1;
    };

    const getOldQuantityForId = (products: CartItemModel[], id: string) => {
        return products
            .filter((p) => p.id === id)
            .filter((p) => !p.free || p.freeEntryAddedByCustomer)
            .map((p) => p.quantity)
            .reduce((q1, q2) => q1 + q2, 0);
    };

    const onQuantityChange = (
        newQuantity: number,
        oldQuantity: number,
        id: string,
        product: CartItemModel
    ) => {
        if (newQuantity === 0) {
            return;
        }

        const oldQuantityForId = getOldQuantityForId(cartItems, id);
        const difference = newQuantity - oldQuantity;
        const newQuantityForSku = oldQuantityForId + difference;

        setIsUpdatingCartForId(id, true);
        updateCartItem(
            product.id!,
            newQuantityForSku,
            deliveryType,
            product,
            difference
        )?.then(() => {
            setIsUpdatingCartForId(id, false);
        });
    };

    const onRemoveFromCart = (
        id: string,
        quantity: number,
        isFreeAddedByCustomer: boolean,
        product: CartItemModel
    ) => {
        setIsUpdatingCartForId(product.id!, true);
        removeFromCart(id, quantity, isFreeAddedByCustomer)?.then(() => {
            setIsUpdatingCartForId(product.id!, false);
        });
    };

    const removeProductFromCart = (product: CartItemModel) =>
        onRemoveFromCart(
            product.id!,
            product.quantity,
            product.freeEntryAddedByCustomer,
            product
        );

    const onUpdateCartItemConfig = (
        id: string,
        config: Record<string, unknown>,
        product: CartItemModel,
        productSubType: string
    ) => {
        setIsUpdatingCartForId(product.id!, true);
        return updateCartItemConfig(id, config, product, productSubType)?.then(
            (data) => {
                setIsUpdatingCartForId(product.id!, false);
                return data;
            }
        );
    };

    const onStorePickerSubmit = (shop: CloudStore, product: CartItemModel) => {
        changeStore
            ? changeStore(shop.code).then((action) => {
                  if (action.type === APIStatus.SUCCESS) {
                      updateCartItem(
                          product.id!,
                          product.quantity,
                          DeliveryType.ClickAndCollect
                      ).then(() => setStorePickerSku(""));
                  }
              })
            : null;
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (wishlistModalVisible) return;

        const isInWishlist = (product: CartItemModel) =>
            wishlists
                .flatMap((wishlist) => wishlist.products)
                .some(({ sku }) => sku === product.sku);

        addToWishlistQueue.current.filter(isInWishlist).forEach((product) => {
            if (!product) return;
            removeProductFromCart(product);
        });

        addToWishlistQueue.current = [];
    }, [wishlistModalVisible, wishlists]);

    const onMoveToWishlist = async (product: CartItemModel) => {
        const addedToWishlistStatus = await addToWishlist(product.sku);

        const statusActions: Partial<
            Record<AddToWishlistStatus, () => unknown>
        > = {
            [AddToWishlistStatus.ADDED]: () => removeProductFromCart(product),
            [AddToWishlistStatus.WAITING_FOR_USER_ACTION]: () =>
                addToWishlistQueue.current.push(product)
        };

        statusActions[addedToWishlistStatus]?.();
    };

    const onAddAdditionalService = async (
        cartItemId: string,
        serviceSku: string,
        product: CartItemModel
    ) => {
        setIsUpdatingCartForId(cartItemId, true);
        await addAdditionalService(cartItemId, serviceSku, product);
        setIsUpdatingCartForId(cartItemId, false);
    };

    const onRemoveAdditionalService = async (cartItemId: string) => {
        setIsUpdatingCartForId(cartItemId, true);
        await removeAdditionalService(cartItemId);
        setIsUpdatingCartForId(cartItemId, false);
    };

    const onAddAccessory = async (
        cartItemId: string,
        sku: string,
        product: Product<ProductFieldSet.ProductTile>
    ) => {
        setIsUpdatingCartForId(cartItemId, true);
        await addAccessory(cartItemId, sku, product);
        setIsUpdatingCartForId(cartItemId, false);
    };

    const onRemoveFromWishlist = (sku: string) => {
        return removeFromWishlist(sku);
    };

    if (cartItems.length === 0) {
        return null;
    }

    return (
        <div>
            <Row
                data-test={`productCollectionBlock-${title}`}
                className="bg-light-gray bg-md-light"
            >
                <div className="col-12">
                    <div className="bg-light-gray d-flex py-2 px-md-3">
                        <h3
                            data-test={`productCollectionBlock-${title}-title`}
                            className="font-weight-bold m-0 py-2"
                        >
                            <i className={cn("las", "mr-2", icon)} />
                            {title}
                        </h3>
                        {infoBlock && (
                            <span
                                data-test={`productCollectionBlock-${title}-infoBlock`}
                                className="d-none d-md-flex align-content-center ml-3"
                            >
                                {infoBlock}
                            </span>
                        )}
                    </div>
                </div>
            </Row>
            {infoBlock && (
                <>
                    <div
                        data-test={`productCollectionBlock-${title}-infoBlock`}
                        className="d-block d-md-none"
                    >
                        <Row>
                            <div className="col-12 mt-2">{infoBlock}</div>
                        </Row>
                    </div>
                    <hr className="d-block d-md-none my-2" />
                </>
            )}
            {cartItems.map((cartItem, index) => (
                <div key={`${cartItem.id}-${index}`}>
                    <CartItem
                        wishlists={wishlists}
                        productCtas={callToActions.find(
                            (cta) => cta.product?.sku === cartItem.sku
                        )}
                        moveToWishlist={onMoveToWishlist}
                        removeFromWishlist={onRemoveFromWishlist}
                        cartItem={cartItem}
                        hasProductMultipleCartEntries={hasProductMultipleCartEntries(
                            cartItem.sku
                        )}
                        removeFromCart={(
                            id: string,
                            quantity: number,
                            isFreeAddedByCustomer: boolean
                        ) =>
                            onRemoveFromCart(
                                id,
                                quantity,
                                isFreeAddedByCustomer,
                                cartItem
                            )
                        }
                        updateCartItemConfig={(
                            id: string,
                            config: Record<string, unknown>,
                            productSubType: string
                        ) =>
                            onUpdateCartItemConfig(
                                id,
                                config,
                                cartItem,
                                productSubType
                            )
                        }
                        onQuantityChange={(newQuantity, oldQuantity) =>
                            onQuantityChange(
                                newQuantity,
                                oldQuantity,
                                cartItem.id!,
                                cartItem
                            )
                        }
                        onRemoveAdditionalService={(cartItemId) =>
                            onRemoveAdditionalService(cartItemId)
                        }
                        onAddAdditionalService={(cartItemId, serviceSku) =>
                            onAddAdditionalService(
                                cartItemId,
                                serviceSku,
                                cartItem
                            )
                        }
                        onAddAccessory={(cartItemId, sku, product) => {
                            return onAddAccessory(cartItemId, sku, product);
                        }}
                        isUpdatingCart={isUpdatingCart[cartItem.id!]}
                        changeDeliveryType={(type) =>
                            changeDeliveryType(type, cartItem)
                        }
                        ccTooltipInfo={ccTooltipInfo}
                        dsvTooltipInfo={dsvTooltipInfo}
                        warrantyDepositTooltipInfo={warrantyDepositTooltipInfo}
                        cashbackTooltipInfo={cashbackTooltipInfo}
                        paintConfiguratorPath={paintConfiguratorPath}
                    />
                    <hr className="my-2" />
                    {changeStore && (
                        <StorePickerModal
                            type={StorePickerType.SELECT}
                            isOpen={storePickerSku === cartItem.sku}
                            storeCode={null}
                            infoLink={null}
                            fixedFeatures={[
                                StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE
                            ]}
                            displayStockLevel={false}
                            onClose={() => setStorePickerSku("")}
                            onSubmit={(shop) =>
                                onStorePickerSubmit(shop, cartItem)
                            }
                            filterStoreType={CloudStoreType.WalkIn}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

export default ProductCollectionBlock;
